import { t } from "@/languages/i18n"
import { normalizeUrl } from "@/utils/ensure-protocol-helper"
import { DeleteOutlined } from "@ant-design/icons"
import { Button, Empty, Input, Tag } from "antd"
import React from "react"
import { MAX_MENU_DEPTH, type MenuNode } from "../types/menu"

interface MenuItemInspectorProps {
  node: MenuNode | null
  depth: number
  disabled?: boolean
  onChange: (patch: Partial<Omit<MenuNode, "children">>) => void
  onIndent: () => void
  onOutdent: () => void
  onRemove: () => void
}

/**
 * Sửa mục đang chọn. Màn cũ bắt bung từng card trong danh sách kéo thả để sửa;
 * tách hẳn ra panel giúp thao tác kéo và thao tác sửa không giẫm chân nhau.
 */
const MenuItemInspector: React.FC<MenuItemInspectorProps> = ({
  node,
  depth,
  disabled,
  onChange,
  onIndent,
  onOutdent,
  onRemove,
}) => {
  if (!node) {
    return (
      <div className="py-8">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("Chọn một mục trong menu để sửa")} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
          {t("Nhãn hiển thị")}
        </label>
        <Input
          value={node.name}
          disabled={disabled}
          placeholder={t("Tên hiển thị trên menu")}
          onChange={event => onChange({ name: event.target.value })}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
          {t("Đường dẫn")}
        </label>
        <Input
          value={node.url}
          disabled={disabled}
          className="font-mono text-xs"
          placeholder="/gioi-thieu"
          onChange={event => onChange({ url: normalizeUrl(event.target.value) })}
        />
        <p className="text-xs text-gray-400 mt-1 mb-0">
          {t("Đường dẫn nội bộ ghi không kèm tên miền. Link ngoài phải bắt đầu bằng http:// hoặc https://")}
        </p>
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{t("Cấp")}</label>
        <div className="flex items-center gap-2">
          <Button size="small" disabled={disabled || depth === 0} onClick={onOutdent}>
            ⇤
          </Button>
          <Tag color="blue" className="!mr-0">
            {t("Cấp {level}").replace("{level}", String(depth + 1))}
          </Tag>
          <Button size="small" disabled={disabled || depth >= MAX_MENU_DEPTH - 1} onClick={onIndent}>
            ⇥
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-1 mb-0">
          {t("Menu hỗ trợ tối đa {max} cấp.").replace("{max}", String(MAX_MENU_DEPTH))}
        </p>
      </div>

      <Button danger icon={<DeleteOutlined />} block disabled={disabled} onClick={onRemove}>
        {t("Xóa khỏi menu")}
      </Button>
    </div>
  )
}

export default MenuItemInspector
